import { useEffect, useState } from 'react';

import {
  ASSURANCE_RATING_MAX,
  ASSURANCE_RATING_MIN,
  DEFAULT_ASSURANCE_RATING
} from 'lib/constants/healthcareProgram/paymentSetup';

import SwitchController from 'components/practice/Settings/components/HealthcareProgram/components/PaymentProgramTab/components/PaymentSettings/components/SwitchController/SwitchController';
import Input from 'components/shared/Input/Input';
import Slider from 'components/shared/Slider/Slider';
import Typography from 'components/shared/Typography/Typography';

const clampThreshold = (n) =>
  Math.max(ASSURANCE_RATING_MIN, Math.min(ASSURANCE_RATING_MAX, Number(n) || 0));

const AssuranceRating = ({ value = {}, onChange }) => {
  const { show_patient_rating = 0, show_on_letter = false } = value;

  const [thresholdText, setThresholdText] = useState(String(show_patient_rating));

  useEffect(() => {
    setThresholdText(String(show_patient_rating));
  }, [show_patient_rating]);

  const handleThreshold = (next) => onChange({ show_patient_rating: clampThreshold(next) });
  const handleShowOnLetter = (next) => onChange({ show_on_letter: next });

  const handleInputChange = (e) => {
    const raw = e.target.value;
    setThresholdText(raw);
    if (raw === '') return;
    const n = Number(raw);
    if (Number.isFinite(n)) handleThreshold(n);
  };

  const handleInputBlur = () => {
    if (thresholdText === '' || !Number.isFinite(Number(thresholdText))) {
      setThresholdText(String(show_patient_rating));
    }
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-2.5">
        <Typography
          variant="label"
          size="small"
          weight="medium"
          iconRight="info-mono"
          tooltip={
            <>
              The assurance rating reflects Myriad’s confidence in the eligibility data returned.
              <br />
              Higher thresholds show ratings only when eligibility confidence is stronger.
            </>
          }>
          Minimum score to show patient rating
        </Typography>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2.5">
            <Input
              id="assurance-threshold-input"
              type="number"
              min={ASSURANCE_RATING_MIN}
              max={ASSURANCE_RATING_MAX}
              value={thresholdText}
              rightText="%"
              hideNumberControls
              onChange={handleInputChange}
              onBlur={handleInputBlur}
            />
            <div className="flex-1">
              <Slider
                value={show_patient_rating}
                onChange={handleThreshold}
                min={ASSURANCE_RATING_MIN}
                max={ASSURANCE_RATING_MAX}
                step={1}
                isTippy={false}
              />
            </div>
          </div>
          <Typography variant="p" size="small" color="neutral-700">
            Recommended range for most practices: {DEFAULT_ASSURANCE_RATING}%
          </Typography>
        </div>
      </div>
      <SwitchController
        title="Show assurance rating on the letter"
        description="When enabled, the patient sees the eligibility assurance rating on their letter (only
            when the score meets the threshold set above)."
        checked={show_on_letter}
        onChange={handleShowOnLetter}
      />
    </div>
  );
};

export default AssuranceRating;
